import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { formatFileSize } from 'shared/utils/fileValidation';
import { resolveRecipientTypes } from 'screens/mass-mail/utils/recipientTypes';
import { RecipientType } from 'screens/mass-mail/types';

/**
 * Maximale gezamenlijke bijlagegrootte voor één reno-mail.
 *
 * De bijlagen gaan base64 mee in de JSON-body van `POST /reno-helpdesk/tickets/:id/send-email`.
 * Die route staat in `main.ts` op 50 MB; base64 is ~1,37x de bestandsgrootte, dus ~36 MB aan
 * bestanden. 35 MB houdt marge voor onderwerp, tekst en de rest van de body.
 *
 * Zonder deze controle liep het mis in de body-parser en zag de gebruiker enkel
 * "Failed to fetch" — een netwerkfout die niets zegt over de echte oorzaak.
 */
export const MAX_RENO_ATTACHMENT_BYTES = 35 * 1024 * 1024;

/**
 * Ontvanger zoals de composer hem aanlevert: een contact uit de contactenlijst of
 * een los CC-/BCC-adres. `recipientType` is de keuze Aan/CC/BCC per contact.
 */
type MailOntvanger = {
  name?: string;
  firstName?: string;
  email?: string;
  recipientType?: RecipientType;
};

/**
 * Verstuurt een mail voor een renovatieticket en koppelt hem automatisch aan het ticket.
 * Uploadt eerst eventuele bijlagen, verdeelt de contacten over TO/CC/BCC volgens de keuze
 * per contact, en zet het kanaal (supplier/tenant) zodat de backend de juiste
 * RS-/RT-code gebruikt.
 *
 * Er hoeft geen TO-ontvanger te zijn: staat iedereen in CC/BCC, dan vertrekt de mail
 * zonder zichtbare geadresseerde. Dat is normaal mailgedrag en de backend laat het toe
 * zolang er érgens een geldige ontvanger staat.
 *
 * `emailData` is de payload zoals de gedeelde MassMailModal die aanlevert
 * (contacts, subject, content, attachedFiles, ccContacts, bccContacts, replyToMessageId).
 */
export async function sendRenoTicketEmail(
  ticketId: number,
  emailData: any,
  channel: 'supplier' | 'tenant',
  session: any,
  /**
   * Waarover deze mail gaat (Offerte, Factuur, …), gekozen bij het verzenden. Wordt per
   * mail bewaard zodat het e-mailoverzicht per mail het type kan tonen — het tickettype
   * kan later nog wijzigen en zegt dus niets over een oude mail. `null` = geen type.
   */
  messageType: string | null = null,
): Promise<any> {
  if (!session || !ticketId) throw new Error('Geen sessie of ticket beschikbaar');

  let uploadedAttachments: any[] = [];
  if (emailData.attachedFiles?.length > 0) {
    const totaal = emailData.attachedFiles.reduce((som: number, f: any) => som + (f?.size ?? 0), 0);
    if (totaal > MAX_RENO_ATTACHMENT_BYTES) {
      throw new Error(
        `De bijlagen zijn samen te groot (${formatFileSize(totaal)}, max ${formatFileSize(
          MAX_RENO_ATTACHMENT_BYTES,
        )}). Verwijder een bestand of stuur het via een downloadlink.`,
      );
    }

    const formData = new FormData();
    emailData.attachedFiles.forEach((f: any) => formData.append('files', f.file));
    const uploadRes = await fetchWithAuth(`${API_URL}/email/upload-attachments`, { method: 'POST', body: formData }, session);
    if (!uploadRes.ok) throw new Error('Bijlagen uploaden mislukt');
    const uploadResult = await uploadRes.json();
    uploadedAttachments = uploadResult.attachments ?? [];
  }

  const contacts: MailOntvanger[] = (emailData.contacts ?? []).filter((c: MailOntvanger) =>
    c?.email?.trim(),
  );
  if (!contacts.length) {
    // Benoem wie er geen adres heeft — anders lijkt het alsof het versturen zelf faalt.
    const zonderAdres = ((emailData.contacts ?? []) as { name?: string; firstName?: string }[])
      .map((c) => c?.name || c?.firstName)
      .filter(Boolean);
    throw new Error(
      zonderAdres.length
        ? `Geen e-mailadres bekend voor ${zonderAdres.join(', ')}. Vul het adres aan bij de contactgegevens.`
        : 'Geen ontvanger met e-mailadres geselecteerd.',
    );
  }

  // Rollen bepalen met dezelfde regel als de contactenlijst in de composer, zodat
  // wat daar "Aan" toont ook echt in het TO-veld belandt.
  const rollen = resolveRecipientTypes(contacts);
  const metRol = (rol: RecipientType) => contacts.filter((_, i) => rollen[i] === rol);

  const dedupe = (adressen: (string | undefined)[], reedsGebruikt: Set<string>): string[] => {
    const resultaat: string[] = [];
    adressen.forEach((adres) => {
      const email = adres?.trim();
      if (!email) return;
      const sleutel = email.toLowerCase();
      if (reedsGebruikt.has(sleutel)) return;
      reedsGebruikt.add(sleutel);
      resultaat.push(email);
    });
    return resultaat;
  };

  // Een adres mag maar in één veld staan; TO wint van CC, CC wint van BCC. De losse
  // CC/BCC-velden van de composer komen bovenop de contacten. BCC wordt uitdrukkelijk
  // niet met CC samengevoegd: die ontvangers moeten verborgen blijven.
  const gebruikt = new Set<string>();
  const losseAdressen = (lijst: MailOntvanger[] | undefined) => (lijst ?? []).map((c) => c?.email);

  // Hoogstens één "Aan" — de composer bewaakt dat, maar een tweede zou hier anders
  // stil verdwijnen. Extra TO-contacten schuiven daarom door naar CC. Alleen het
  // gekozen adres wordt als "gebruikt" gemarkeerd; de rest moet CC nog kunnen halen.
  const toContacten = metRol('to');
  const to = toContacten.map((c) => c.email?.trim()).find(Boolean) ?? '';
  if (to) gebruikt.add(to.toLowerCase());

  const cc = dedupe(
    [
      ...toContacten.slice(1).map((c) => c.email),
      ...metRol('cc').map((c) => c.email),
      ...losseAdressen(emailData.ccContacts),
    ],
    gebruikt,
  );
  const bcc = dedupe(
    [...metRol('bcc').map((c) => c.email), ...losseAdressen(emailData.bccContacts)],
    gebruikt,
  );

  const res = await fetchWithAuth(
    `${API_URL}/reno-helpdesk/tickets/${ticketId}/send-email`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to,
        subject: emailData.subject,
        content: emailData.content,
        cc,
        bcc,
        channel,
        messageType,
        attachments: uploadedAttachments,
        replyToMessageId: emailData.replyToMessageId ?? null,
      }),
    },
    session,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'E-mail versturen mislukt');
  }
  return res.json();
}
